import * as CL from '@design-system/component-library';
import { useImperativeHandle, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { dsClass } from '../../../../common/constants/dsClasses.js';
import { t } from '../../../../common/i18n/index.js';
import { scrollTo } from '../../../../common/utils/browserUtils.js';
import { isSoftwareSubCategory } from '../../../../common/utils/productOfferingsUtils.js';
import { POBoxWarningModal } from '../../../Modal/POBoxWarningModal.js';
import { useDeliveryValidation } from '../../hooks/useDeliveryValidation.js';
import { flattenDeliveryOfferings, getDeliveryModeForOffering } from '../../utils/deliveryMethodUtilsCoCo.js';
import { isPOBoxDelivery } from '../../utils/placeOrderUtils.js';
import { StepError } from '../StepError.js';
import { CheckoutFooterCoCo } from '../common/CheckoutFooterCoCo.js';
import { StepContent } from '../common/StepContent.js';
import { AddressSelectionCoCo } from './AddressSelectionCoCo';
import { DeliveryMethodListCoCo } from './DeliveryMethodListCoCo.js';
import { DeliveryOptionSelectionCoCo } from './DeliveryOptionSelectionCoCo.js';
import type { DeliveryMethodGroup } from '../../../../generated/api/deliveryMethodGroup.js';
import type { DeliveryOptionsResponse } from '../../../../generated/api/deliveryOptionsResponse.js';
import type { CustomerPrimaryAddress, PlaceOrderCoCoProps } from '../../PlaceOrderCoCo.js';
import type { CheckoutStepValidationHandleCoCo, PlaceOrderFormDataCoCo } from '../../placeOrderTypesCoCo.js';
import type { JSX, Ref } from 'react';

export interface DeliveryStepCoCoProps extends PlaceOrderCoCoProps {
  onNext: () => void;
  onPrevious: () => void;
  orderSummary?: JSX.Element;
  deliveryMethodGroups?: DeliveryMethodGroup[];
  deliveryOptions?: DeliveryOptionsResponse;
  customerPrimaryAddress?: CustomerPrimaryAddress;
  validationRef?: Ref<CheckoutStepValidationHandleCoCo>;
}

export const DeliveryStepCoCo = ({
  onNext,
  onPrevious,
  customerInfo,
  orderSummary,
  deliveryMethodGroups,
  deliveryOptions,
  customerPrimaryAddress,
  shoppingBasket,
  initialContacts,
  validationRef,
}: DeliveryStepCoCoProps) => {
  const { watch } = useFormContext<PlaceOrderFormDataCoCo>();
  const [showPOBoxWarningModal, setShowPOBoxWarningModal] = useState(false);
  const delivery = watch('delivery');
  const placeOrderItemUnits = watch('products.placeOrderItemUnits');
  const allDeliveryOfferings = flattenDeliveryOfferings(deliveryMethodGroups);
  const selectedDeliveryMode = getDeliveryModeForOffering(deliveryMethodGroups, delivery.deliveryType);

  const isSoftwareOnlyBasket = Boolean(
    shoppingBasket.productOfferings?.length &&
      shoppingBasket.productOfferings.every(item => isSoftwareSubCategory(item.productOfferingType?.subcategory ?? ''))
  );
  const nothingToDeliver = !isSoftwareOnlyBasket && allDeliveryOfferings.length === 0;

  const { addressError, deliveryMethodError, clearAddressError, clearDeliveryMethodError, validate } =
    useDeliveryValidation({
      hasDeliveryAddress: isSoftwareOnlyBasket || nothingToDeliver || Boolean(delivery.deliveryAddress),
      hasDeliveryOfferings: isSoftwareOnlyBasket || nothingToDeliver || allDeliveryOfferings.length > 0,
      hasSelectedDeliveryMethod: isSoftwareOnlyBasket || nothingToDeliver || Boolean(delivery.deliveryType),
    });

  const validateDeliveryStep = (): boolean => {
    if (isSoftwareOnlyBasket || nothingToDeliver) {
      return true;
    }

    if (!validate()) {
      return false;
    }

    if (isPOBoxDelivery(delivery.deliveryAddress?.address.postalCode, selectedDeliveryMode)) {
      setShowPOBoxWarningModal(true);
      return false;
    }

    return true;
  };

  useImperativeHandle(validationRef, () => ({ validate: validateDeliveryStep }));

  const handleNext = async () => {
    if (validateDeliveryStep()) {
      onNext();
    }
    scrollTo(0);
  };

  const selectedContactId = placeOrderItemUnits?.[0]?.userContact;
  const selectedContact = initialContacts.find(c => c.contactId === selectedContactId);
  const selectedEmail = selectedContact?.person?.email ?? selectedContact?.userContact?.email ?? '';

  let deliveryStepContent: JSX.Element;

  if (isSoftwareOnlyBasket) {
    deliveryStepContent = (
      <>
        <p>{t.I4F9('Software license will be delivered to {}', selectedEmail)}</p>
        {orderSummary}
        <CheckoutFooterCoCo onNext={onNext} onPrevious={onPrevious} />
      </>
    );
  } else if (nothingToDeliver) {
    deliveryStepContent = (
      <>
        <CL.Disclaimer
          className={dsClass.MARGIN_BOTTOM_4}
          disclaimerType="info"
          icon={<CL.Icon icon="information" />}
          text={t.FPAP('Your order includes products that do not require delivery.')}
        />
        {orderSummary}
        <CheckoutFooterCoCo onNext={handleNext} onPrevious={onPrevious} />
      </>
    );
  } else {
    deliveryStepContent = (
      <>
        <StepError
          errors={[
            ...(addressError ? [{ text: addressError, id: 'address-selection-id' }] : []),
            ...(deliveryMethodError ? [{ text: deliveryMethodError, id: 'delivery-method-id' }] : []),
          ]}
        />
        <StepContent id="address-selection-id" errorText={addressError}>
          <AddressSelectionCoCo
            customerInfo={customerInfo}
            customerPrimaryAddress={customerPrimaryAddress}
            onClearError={clearAddressError}
          />
        </StepContent>
        <StepContent id="delivery-option-id">
          <DeliveryOptionSelectionCoCo deliveryOptions={deliveryOptions} />
        </StepContent>
        <StepContent id="delivery-method-id" errorText={deliveryMethodError}>
          <DeliveryMethodListCoCo deliveryMethodGroups={deliveryMethodGroups} onClearError={clearDeliveryMethodError} />
        </StepContent>
        {orderSummary}
        <CheckoutFooterCoCo onNext={handleNext} onPrevious={onPrevious} />
      </>
    );
  }

  return (
    <div className="of-place-order__delivery-step of-place-order__delivery-step--coco">
      {showPOBoxWarningModal && <POBoxWarningModal setShowModal={setShowPOBoxWarningModal} />}
      <h1>{t.BSE3('Delivery')}</h1>
      {deliveryStepContent}
    </div>
  );
};
